import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Sparkles, Mic, Users, Zap, Dices, Hourglass, Trophy, User, LogIn, Play, Star } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage, LanguageToggle } from '@/hooks/useLanguage';

const games = [
  {
    id: 'loup-garou',
    emoji: '🐺',
    accentHsl: '0 72% 45%',
    emojiGlow: 'emoji-glow-red',
    available: true,
    inDevelopment: false,
    featured: true,
  },
  {
    id: 'impro',
    emoji: '🎭',
    accentHsl: '262 83% 58%',
    emojiGlow: 'emoji-glow-purple',
    available: true,
    inDevelopment: true,
    featured: false,
  },
  {
    id: 'tribunal',
    emoji: '⚖️',
    accentHsl: '45 93% 47%',
    emojiGlow: 'emoji-glow-amber',
    available: true,
    inDevelopment: true,
    featured: false,
  },
  {
    id: 'appel-absurde',
    emoji: '📞',
    accentHsl: '160 84% 39%',
    emojiGlow: 'emoji-glow-emerald',
    available: true,
    inDevelopment: true,
    featured: false,
  },
  {
    id: 'murder-mystery',
    emoji: '🔍',
    accentHsl: '350 84% 60%',
    emojiGlow: 'emoji-glow-crimson',
    available: true,
    inDevelopment: true,
    featured: false,
  },
  {
    id: 'rpg-ia',
    emoji: '🗡️',
    accentHsl: '270 67% 47%',
    emojiGlow: 'emoji-glow-violet',
    available: true,
    inDevelopment: true,
    featured: false,
  },
  {
    id: 'startup-pitch',
    emoji: '💼',
    accentHsl: '160 84% 39%',
    emojiGlow: 'emoji-glow-emerald',
    available: true,
    inDevelopment: true,
    featured: false,
  },
];

const features = [
  {
    icon: Mic,
    title: 'Narration Vocale IA',
    description: 'Une IA unique raconte et anime vos parties',
  },
  {
    icon: Users,
    title: 'Multijoueur Local',
    description: 'Jouez ensemble, un seul appareil suffit',
  },
  {
    icon: Zap,
    title: 'Instantané',
    description: 'Aucune inscription, gratuit, jouez maintenant',
  },
];

const ROTATING_WORDS = ['Narrateur', 'Animateur', 'Partenaire de Jeu'];

const UserButton = () => {
  const { user, profile, loading } = useAuth();

  if (loading) {
    return <div className="w-10 h-10 rounded-full bg-card/30 animate-pulse" />;
  }

  if (user) {
    return (
      <Link 
        to="/profile"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 border border-accent/40 text-accent hover:bg-accent/30 transition-colors"
      >
        <User className="w-4 h-4" />
        <span className="hidden sm:inline">{profile?.username || 'Mon profil'}</span>
      </Link>
    );
  }

  return (
    <Link 
      to="/auth"
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground transition-colors"
    >
      <LogIn className="w-4 h-4" />
      <span className="hidden sm:inline">Connexion</span>
    </Link>
  );
};

const Platform = () => {
  const [wordIndex, setWordIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const { t, language } = useLanguage();

  const ROTATING_WORDS_I18N = [
    t('platform.hero.word1'),
    t('platform.hero.word2'),
    t('platform.hero.word3'),
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setWordIndex((prev) => (prev + 1) % ROTATING_WORDS_I18N.length);
        setIsAnimating(false);
      }, 300);
    }, 3000);

    return () => clearInterval(interval);
  }, [language]);

  const featuredGame = games.find(g => g.featured);
  const otherGames = games.filter(g => !g.featured);

  return (
    <>
      <Helmet>
        <title>SuperSociety.ai - Jeux de Société animés par Intelligence Artificielle</title>
        <meta 
          name="description" 
          content="SuperSociety.ai - Découvrez nos jeux de société interactifs animés par IA vocale. Loup-Garou, et bientôt d'autres expériences immersives sans animateur." 
        />
      </Helmet>
      
      <div className="min-h-screen bg-background relative overflow-hidden">
        {/* Premium animated background */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {/* Main cosmic glow */}
          <div 
            className="absolute top-[-30%] left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full opacity-40"
            style={{
              background: 'radial-gradient(circle, hsl(262 83% 58% / 0.15) 0%, hsl(0 72% 51% / 0.08) 40%, transparent 70%)',
            }}
          />
          
          {/* Floating accent orbs with animation */}
          <div 
            className="absolute top-[15%] left-[5%] w-[400px] h-[400px] rounded-full opacity-30 animate-pulse"
            style={{
              background: 'radial-gradient(circle, hsl(0 72% 45% / 0.25) 0%, transparent 60%)',
              animationDuration: '5s',
            }}
          />
          <div 
            className="absolute top-[60%] right-[8%] w-[350px] h-[350px] rounded-full opacity-25 animate-pulse"
            style={{
              background: 'radial-gradient(circle, hsl(262 83% 58% / 0.2) 0%, transparent 60%)',
              animationDuration: '7s',
              animationDelay: '2s',
            }}
          />
          <div 
            className="absolute bottom-[20%] left-[15%] w-[250px] h-[250px] rounded-full opacity-20 animate-pulse"
            style={{
              background: 'radial-gradient(circle, hsl(47 100% 72% / 0.25) 0%, transparent 60%)',
              animationDuration: '6s',
              animationDelay: '1s',
            }}
          />
          
          {/* Subtle grid pattern */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `
                linear-gradient(hsl(var(--foreground)) 1px, transparent 1px),
                linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)
              `,
              backgroundSize: '80px 80px',
            }}
          />

          {/* Gradient mesh overlay */}
          <div 
            className="absolute inset-0 opacity-50"
            style={{
              background: 'radial-gradient(ellipse 50% 50% at 50% 0%, transparent 0%, hsl(var(--background)) 100%)',
            }}
          />
        </div>

        <div className="relative z-10 container mx-auto px-4 sm:px-6 py-8 md:py-16">
          {/* Top Navigation */}
          <nav className="flex items-center justify-end gap-3 mb-12 slide-up">
            <LanguageToggle />
            <Link 
              to="/leaderboards"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/40 border border-border/40 text-muted-foreground hover:text-foreground hover:border-border/60 hover:bg-card/60 transition-all duration-300"
            >
              <Trophy className="w-4 h-4" />
              <span className="hidden sm:inline">{t('nav.leaderboards')}</span>
            </Link>
            <UserButton />
          </nav>

          {/* Header */}
          <header className="text-center mb-16 md:mb-24 space-y-8">
            {/* Logo & Brand */}
            <div className="slide-up">
              <Link to="/" className="inline-flex items-center gap-4 group">
                <div className="relative">
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-gradient-to-br from-accent via-primary to-moon flex items-center justify-center shadow-2xl shadow-accent/40 group-hover:shadow-accent/60 transition-all duration-500 group-hover:scale-105">
                    <Dices className="w-9 h-9 md:w-11 md:h-11 text-background" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-moon animate-pulse shadow-lg shadow-moon/50" />
                </div>
                <div className="text-left">
                  <h2 className="font-display text-3xl md:text-4xl text-foreground tracking-tight">
                    Super<span className="text-accent">Society</span><span className="text-moon">.ai</span>
                  </h2>
                  <p className="text-sm text-muted-foreground tracking-widest uppercase">{t('platform.tagline')}</p>
                </div>
              </Link>
            </div>

            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-accent/15 to-primary/15 border border-accent/25 text-accent text-sm font-medium slide-up backdrop-blur-sm" style={{ animationDelay: '0.05s' }}>
              <Sparkles className="w-4 h-4" />
              {t('platform.badge')}
            </div>
            
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl text-foreground leading-[1.1] slide-up" style={{ animationDelay: '0.1s' }}>
              {t('platform.hero.line1')}
              <br />
              <span 
                className={`inline-block bg-gradient-to-r from-moon via-accent to-primary bg-clip-text text-transparent transition-all duration-300 ${isAnimating ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}
              >
                {ROTATING_WORDS_I18N[wordIndex]}
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed slide-up" style={{ animationDelay: '0.2s' }}>
              {t('platform.description')}
            </p>
          </header>

          {/* Features - More visual */}
          <section className="max-w-4xl mx-auto mb-20 md:mb-28">
            <div className="grid md:grid-cols-3 gap-4 md:gap-6">
              {features.map((feature, index) => (
                <div 
                  key={feature.title}
                  className="relative group text-center p-6 rounded-2xl bg-gradient-to-b from-card/60 to-card/30 border border-border/30 hover:border-accent/40 transition-all duration-500 slide-up backdrop-blur-sm hover:bg-card/50"
                  style={{ animationDelay: `${0.3 + index * 0.1}s` }}
                >
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative space-y-4">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-accent/20 to-accent/5 border border-accent/30 group-hover:border-accent/50 transition-colors shadow-lg shadow-accent/10">
                      <feature.icon className="w-7 h-7 text-accent" />
                    </div>
                    <h3 className="font-display text-lg text-foreground">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Featured Game - Hero Card */}
          {featuredGame && (
            <section className="max-w-4xl mx-auto mb-16 slide-up" style={{ animationDelay: '0.5s' }}>
              <FeaturedGameCard game={featuredGame} />
            </section>
          )}

          {/* Games Grid */}
          <section className="max-w-5xl mx-auto">
            <h2 className="font-display text-2xl md:text-3xl text-foreground text-center mb-10 slide-up" style={{ animationDelay: '0.6s' }}>
              {t('platform.features')}
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {otherGames.map((game, index) => (
                <GameCard key={game.id} game={game} index={index} />
              ))}
            </div>
          </section>

          {/* Footer */}
          <footer className="mt-28 text-center space-y-6">
            <div className="inline-flex items-center gap-3 text-muted-foreground">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent/20 to-primary/20 flex items-center justify-center border border-accent/20">
                <Dices className="w-5 h-5 text-accent" />
              </div>
              <span className="font-display text-xl">Super<span className="text-accent">Society</span><span className="text-moon">.ai</span></span>
            </div>
            <p className="text-sm text-muted-foreground/70">
              {t('platform.footer')}
            </p>
          </footer>
        </div>
      </div>
    </>
  );
};

interface GameCardProps {
  game: typeof games[0];
  index: number;
}

const FeaturedGameCard = ({ game }: { game: typeof games[0] }) => {
  const { t } = useLanguage();
  
  const gameName = t(`game.${game.id}.name`);
  const gameDescription = t(`game.${game.id}.description`);
  const gamePlayers = t(`game.${game.id}.players`);
  
  return (
    <Link 
      to={`/${game.id}`}
      className="group relative block overflow-hidden rounded-3xl border-2 transition-all duration-700 hover:scale-[1.02]"
      style={{
        borderColor: `hsl(${game.accentHsl} / 0.4)`,
        background: `linear-gradient(135deg, hsl(${game.accentHsl} / 0.15) 0%, hsl(var(--card)) 50%, hsl(${game.accentHsl} / 0.08) 100%)`,
      }}
    >
      {/* Animated glow border */}
      <div 
        className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"
        style={{
          boxShadow: `inset 0 0 60px hsl(${game.accentHsl} / 0.2), 0 0 80px hsl(${game.accentHsl} / 0.15)`,
        }}
      />
      
      {/* Background glow effect */}
      <div 
        className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-30 group-hover:opacity-50 transition-opacity duration-700"
        style={{
          background: `radial-gradient(circle, hsl(${game.accentHsl} / 0.4) 0%, transparent 60%)`,
          transform: 'translate(30%, -30%)',
        }}
      />

      <div className="relative p-8 md:p-12 flex flex-col md:flex-row items-center gap-8 md:gap-12">
        {/* Emoji */}
        <div className="relative">
          <div 
            className={`text-8xl md:text-9xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-700 ${game.emojiGlow}`}
          >
            {game.emoji}
          </div>
          {/* Floating particles */}
          <div 
            className="absolute top-1/2 left-1/2 w-40 h-40 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-0 group-hover:opacity-60 transition-opacity duration-700"
            style={{
              background: `radial-gradient(circle, hsl(${game.accentHsl} / 0.3) 0%, transparent 70%)`,
            }}
          />
        </div>

        {/* Content */}
        <div className="flex-1 text-center md:text-left space-y-5">
          <div className="flex items-center justify-center md:justify-start gap-3">
            <h3 
              className="font-display text-3xl md:text-4xl lg:text-5xl transition-all duration-500"
              style={{ color: `hsl(${game.accentHsl})` }}
            >
              {gameName}
            </h3>
            <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-moon/20 border border-moon/30">
              <Star className="w-4 h-4 text-moon fill-moon" />
              <span className="text-xs font-medium text-moon">{t('game.popular')}</span>
            </div>
          </div>
          
          <p className="text-lg text-muted-foreground max-w-md leading-relaxed">
            {gameDescription}
          </p>
          
          <div className="flex items-center justify-center md:justify-start gap-4 pt-2">
            <span 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border"
              style={{
                backgroundColor: `hsl(${game.accentHsl} / 0.1)`,
                borderColor: `hsl(${game.accentHsl} / 0.3)`,
                color: `hsl(${game.accentHsl})`,
              }}
            >
              <Users className="w-4 h-4" />
              {gamePlayers}
            </span>
            
            <span 
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-500 group-hover:gap-3"
              style={{
                backgroundColor: `hsl(${game.accentHsl})`,
                color: 'hsl(var(--background))',
                boxShadow: `0 0 30px hsl(${game.accentHsl} / 0.4)`,
              }}
            >
              <Play className="w-4 h-4" />
              {t('game.play')}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

const GameCard = ({ game, index }: GameCardProps) => {
  const { t } = useLanguage();
  
  const gameName = t(`game.${game.id}.name`);
  const gameDescription = t(`game.${game.id}.description`);
  const gamePlayers = t(`game.${game.id}.players`);

  if (!game.available) {
    return (
      <div 
        className="relative rounded-2xl border-2 p-6 opacity-50 cursor-not-allowed transition-all duration-500 slide-up backdrop-blur-sm"
        style={{ 
          animationDelay: `${0.7 + index * 0.08}s`,
          borderColor: `hsl(${game.accentHsl} / 0.2)`,
          background: `linear-gradient(145deg, hsl(${game.accentHsl} / 0.08) 0%, hsl(var(--card) / 0.5) 100%)`,
        }}
      >
        <div className={`text-5xl mb-4 opacity-60 ${game.emojiGlow}`}>{game.emoji}</div>
        <h3 
          className="font-display text-xl mb-2"
          style={{ color: `hsl(${game.accentHsl} / 0.7)` }}
        >
          {gameName}
        </h3>
        <p className="text-sm text-muted-foreground/60 line-clamp-2">{gameDescription}</p>
        <div className="absolute top-4 right-4">
          <span className="text-xs text-muted-foreground/50 px-2 py-1 rounded-full border border-border/30">
            {t('game.coming_soon')}
          </span>
        </div>
      </div>
    );
  }

  return (
    <Link 
      to={`/${game.id}`}
      className="group relative rounded-2xl border-2 p-6 transition-all duration-500 slide-up backdrop-blur-sm hover:scale-[1.03]"
      style={{ 
        animationDelay: `${0.7 + index * 0.08}s`,
        borderColor: `hsl(${game.accentHsl} / 0.25)`,
        background: `linear-gradient(145deg, hsl(${game.accentHsl} / 0.1) 0%, hsl(var(--card) / 0.6) 50%, hsl(${game.accentHsl} / 0.05) 100%)`,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = `hsl(${game.accentHsl} / 0.5)`;
        e.currentTarget.style.boxShadow = `0 20px 60px -15px hsl(${game.accentHsl} / 0.25), 0 0 40px hsl(${game.accentHsl} / 0.1)`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = `hsl(${game.accentHsl} / 0.25)`;
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Hover glow overlay */}
      <div 
        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: `radial-gradient(circle at 50% 0%, hsl(${game.accentHsl} / 0.15) 0%, transparent 60%)`,
        }}
      />
      
      <div className="relative">
        {/* In Development Badge */}
        {game.inDevelopment && (
          <div className="absolute top-0 right-0">
            <span 
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border"
              style={{
                backgroundColor: 'hsl(45 93% 47% / 0.1)',
                borderColor: 'hsl(45 93% 47% / 0.3)',
                color: 'hsl(45 93% 60%)',
              }}
            >
              <Hourglass className="w-3 h-3" />
              {t('game.in_dev')}
            </span>
          </div>
        )}
        
        <div className={`text-5xl md:text-6xl mb-5 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500 ${game.emojiGlow}`}>
          {game.emoji}
        </div>
        
        <h3 
          className="font-display text-xl md:text-2xl mb-3 transition-all duration-300"
          style={{ color: `hsl(${game.accentHsl})` }}
        >
          {gameName}
        </h3>
        
        <p className="text-sm text-muted-foreground mb-5 leading-relaxed line-clamp-2">
          {gameDescription}
        </p>
        
        <div className="flex items-center justify-between">
          <span 
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border"
            style={{
              backgroundColor: `hsl(${game.accentHsl} / 0.08)`,
              borderColor: `hsl(${game.accentHsl} / 0.2)`,
              color: 'hsl(var(--foreground) / 0.7)',
            }}
          >
            <Users className="w-3 h-3" />
            {gamePlayers}
          </span>
          
          <span 
            className="text-sm font-medium opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center gap-1"
            style={{ color: `hsl(${game.accentHsl})` }}
          >
            {t('game.play')}
            <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
          </span>
        </div>
      </div>
    </Link>
  );
};

export default Platform;
